import { FunctionComponent, useState, useRef } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, IconButton, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { CropperImage, MobilePhotoSourceSheet } from 'components/UI';
import clsx from 'clsx';
import { MAX_IMAGE_UPLOAD_BYTES } from 'utils/ImageResizeUtils';
import { buildMimeExtensionChecker, normalizeImageToJpegIfFormatNotAllowed } from 'utils/imageFormatToJpeg';
import { getGalleryFileAccept, isIOSDevice } from 'utils/mobileImageGalleryAcept';
export interface Props {
    imageUrl?: string;
    onChangeImage: (imageFile: File | undefined, image: string | undefined) => void;
    maxSize: number;
    maxWidth: number;
    maxHeight: number;
    imageTypes: Array<string>;
    className?: string;
    containerClassName?: string;
    type?: 'square' | 'circle';
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column'
        },
        imageContainer: {
            borderRadius: (props: Props) => (props.type === 'square' ? '14.15px' : '50%'),
            height: '134px',
            width: '134px'
        },
        emptyImage: {
            alignItems: 'center',
            backgroundColor: '#F1F1F1',
            borderRadius: (props: Props) => (props.type === 'square' ? '14.15px' : '50%'),
            color: '#D3D3D3',
            cursor: 'pointer',
            display: 'flex',
            fontSize: '33px',
            height: '100%',
            justifyContent: 'center',
            width: '100%'
        },
        image: {
            backgroundImage: (props: Props) => 'url(' + props.imageUrl + ')',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            borderRadius: (props: Props) => (props.type === 'square' ? '14.15px' : '50%'),
            height: '100%',
            position: 'relative',
            width: '100%'
        },
        imageClickable: {
            cursor: 'pointer'
        },
        trashButton: {
            '&:hover': {
                backgroundColor: '#FAFAFA'
            },
            'backgroundColor': '#FFFFFF',
            'fontSize': '14px',
            'height': '31.36px',
            'position': 'absolute',
            'right': '5%',
            'top': '5%',
            'width': '31.36px'
        },
        descriptionContainer: {
            marginTop: '16px'
        },
        description: {
            color: '#7A7A7A',
            fontSize: '14px',
            paddingBottom: '8px',
            lineHeight: '21px'
        },
        error: {
            color: '#EF4F57',
            fontSize: '14px',
            lineHeight: '14px',
            marginTop: '16px'
        }
    })
);

export const UploadImage: FunctionComponent<Props> = props => {
    const classes = useStyles(props);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTouchMobile =
        typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    const useActionsSheet = isMobile || isTouchMobile;

    const [showCropper, setShowCropper] = useState(false);
    const [error, setError] = useState<string>();
    const [selectedImage, setSelectedImage] = useState<string>();
    const [fileName, setFileName] = useState<string>();
    const [showActionsSheet, setShowActionsSheet] = useState(false);

    const input = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const imageTypeDescription = () => {
        return props.imageTypes.map(imgType => imgType.toUpperCase()).join(' or ');
    };

    const fileAccept = props.imageTypes.map(ext => `.${ext}`).join(',');

    const isFileTypeAllowed = buildMimeExtensionChecker(props.imageTypes);

    const handleChangeClick = () => {
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        if (useActionsSheet) {
            setShowActionsSheet(true);
        } else {
            input.current?.click();
        }
    };

    const openGalleryPicker = () => {
        galleryInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const openCameraPicker = () => {
        cameraInputRef.current?.click();
        setShowActionsSheet(false);
    };

    const handleRemoveEdit = () => {
        props.onChangeImage && props.onChangeImage(undefined, undefined);
    };

    const handleImageAreaClickMobile = () => {
        if (!useActionsSheet) return;
        if (isIOSDevice()) {
            openGalleryPicker();
            return;
        }
        setShowActionsSheet(true);
    };

    const selectImageHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        const rawFile = inputEl.files?.[0];
        inputEl.value = '';

        if (!rawFile) return;

        let file = rawFile;
        try {
            file = await normalizeImageToJpegIfFormatNotAllowed(rawFile, isFileTypeAllowed);
        } catch {
            setError(`Unsupported file format. Please upload a ${imageTypeDescription()} file.`);
            return;
        }

        const isFileSizeValid = file.size <= MAX_IMAGE_UPLOAD_BYTES;

        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                if (isFileSizeValid) {
                    setSelectedImage(reader.result?.toString());
                    setFileName(file.name);
                    setShowCropper(true);
                    setError('');
                } else {
                    setError(`Image size exceeds the ${MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024)}MB limit.`);
                }
            }
        };

        reader.readAsDataURL(file);
    };

    const cropHandler = (file: File) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                props.onChangeImage && props.onChangeImage(file, reader.result?.toString()!);
                setShowCropper(false);
            }
        };
        reader.readAsDataURL(file);
    };

    return (
        <>
            <Box
                className={clsx(classes.container, props.className)}
                style={{ position: 'relative' }}
            >
                {useActionsSheet ? (
                    <>
                        <input
                            style={{
                                position: 'absolute',
                                width: 0,
                                height: 0,
                                opacity: 0,
                                overflow: 'hidden'
                            }}
                            type="file"
                            ref={galleryInputRef}
                            onChange={selectImageHandler}
                            accept={isIOSDevice() ? getGalleryFileAccept() : fileAccept}
                            aria-hidden
                            tabIndex={-1}
                        />
                        <input
                            style={{
                                position: 'absolute',
                                width: 0,
                                height: 0,
                                opacity: 0,
                                overflow: 'hidden'
                            }}
                            type="file"
                            ref={cameraInputRef}
                            onChange={selectImageHandler}
                            accept="image/*"
                            capture="environment"
                            aria-hidden
                            tabIndex={-1}
                        />
                    </>
                ) : (
                    <input
                        style={{ display: 'none' }}
                        type={'file'}
                        ref={input}
                        onChange={selectImageHandler}
                        accept={fileAccept}
                        value={''}
                    />
                )}

                <Box className={clsx(classes.imageContainer, props.containerClassName)}>
                    {props.imageUrl ? (
                        <Box
                            className={clsx(classes.image, useActionsSheet && classes.imageClickable)}
                            onClick={handleImageAreaClickMobile}
                        >
                            <IconButton
                                className={classes.trashButton}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleRemoveEdit();
                                }}
                            >
                                <FontAwesomeIcon icon={faTrashCan} />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box
                            className={classes.emptyImage}
                            onClick={handleChangeClick}
                        >
                            <FontAwesomeIcon icon={faImage} />
                        </Box>
                    )}
                </Box>

                <Box className={classes.descriptionContainer}>
                    <Typography className={classes.description}>{`Recommended Ratio: ${props.maxWidth}x${
                        props.maxHeight
                    }, ${imageTypeDescription()}`}</Typography>
                    <Typography className={clsx(classes.description)}>{`Max Size: 12MB`}</Typography>
                    {error && <Typography className={clsx(classes.error)}>{error}</Typography>}
                </Box>
            </Box>
            {useActionsSheet && (
                <MobilePhotoSourceSheet
                    open={showActionsSheet}
                    onClose={() => setShowActionsSheet(false)}
                    onPhotoGallery={openGalleryPicker}
                    onCamera={openCameraPicker}
                />
            )}
            {showCropper && (
                <CropperImage
                    image={selectedImage!}
                    fileName={fileName!}
                    cropWidth={props.maxWidth}
                    cropHeight={props.maxHeight}
                    onClose={() => setShowCropper(false)}
                    onCrop={cropHandler}
                    viewScale={props.type === 'square' ? 0.5 : 1}
                />
            )}
        </>
    );
};

export default UploadImage;
